import { isPastDbTimestamp, parseDbTimestamp } from "@repo/shared";
import type { AppBootstrapSsoProviderChange } from "@repo/shared/types";
import { AlertTriangle, X } from "lucide-react";
import { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/ui/link";
import { useT } from "@/lib/i18n/use-t";
import { hasPermissionType, type Permission } from "@/lib/permissions";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { clearSsoLoginMarker, readSsoLoginMarker } from "@/lib/sso-login-marker";
import { T } from "./sso-provider-change-banner.i18n";

type SsoProviderChangeBannerProps = {
  change: AppBootstrapSsoProviderChange | null;
  permissions: string[];
};

/**
 * QL-345 — shown after every SSO sign-in that still went through the old slug's
 * provider id, until the change is confirmed. Administrators get a link to the
 * SSO settings page where the change is confirmed.
 */
export function SsoProviderChangeBanner({ change, permissions }: SsoProviderChangeBannerProps) {
  const t = useT(T);
  const { formatDate } = useRegionalFormat();
  const [marker, setMarker] = useState(readSsoLoginMarker);

  if (!change || marker !== change.currentProviderId) return null;

  const isAdmin = hasPermissionType(permissions as Permission[], "admin");
  const confirmByDate = parseDbTimestamp(change.confirmBy);
  const overdue = isPastDbTimestamp(change.confirmBy);
  const body = !change.confirmBy
    ? t.bodyNoDate
    : (overdue ? t.bodyOverdue : t.body)(confirmByDate ? formatDate(confirmByDate) : "");

  const dismiss = () => {
    clearSsoLoginMarker();
    setMarker(null);
  };

  return (
    <Alert variant="warning" className="mb-6 pr-12">
      <AlertTriangle />
      <AlertTitle>{t.title}</AlertTitle>
      <AlertDescription className="space-y-2">
        <p className="text-muted-foreground">{body}</p>
        {isAdmin ? (
          <Button asChild size="sm">
            <Link to="/settings/sso" onClick={dismiss}>
              {t.confirmLink}
            </Link>
          </Button>
        ) : (
          <p className="text-muted-foreground">{t.tellAdmin}</p>
        )}
      </AlertDescription>
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-2 top-2 size-7"
        onClick={dismiss}
        tooltip={t.dismiss}
      >
        <X aria-hidden="true" />
      </Button>
    </Alert>
  );
}
